import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColumns, GridRow as DataGridRow, GridSortModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaQuestionCircle, FaTimes, FaUpload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { format, parseISO, isValid } from 'date-fns';
import { formatCyDate, notif } from '../../utils/util';
import { getEuStats, getEuList, getEuImportStatus, startEuImport } from '../../../server/server';
import HelpPageModal from '../../modals/HelpPageModal';

const POLL_INTERVAL = 2000;

function EuInsert() {
  const navigate = useNavigate();
  const [selSet, setSelSet] = useState<any>(null);
  const [importStarted, setImportStarted] = useState(false);
  const [helpModal, setHelpModal] = useState(false);

  // Import progress state
  const [importStatus, setImportStatus] = useState<any>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [stats, setStats] = useState<any>({
    date: '2023-04-16T21:09:55.731Z',
    groupNo: 0,
    groupSetName: '',
    name: '',
    totalRows: 0,
    updatedRows: 0,
    addedRows: 0,
  });

  // Table state
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [totalTableRows, setTotalTableRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  // Search/filter state
  const [searchFilters, setSearchFilters] = useState({
    id: '',
    recordId: '',
    celexNo: '',
    officialJournal: '',
    descriptionEN: '',
    pages: '',
    dateOfPublication: '',
    dateOfImport: '',
    dateOfLastUpdate: '',
  });

  const openDetailModal = (row: any) => {
    setSelectedRow(row);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedRow(null);
  };

  const closeHelpModal = () => {
    setHelpModal(false);
  };

  const updateProgress = (data: any) => {
    setImportStatus(data);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const handleSearch = useCallback(() => {
    setPage(0);
    loadEuList(0, pageSize, sortModel[0]?.field ?? 'id', sortModel[0]?.sort?.toUpperCase() ?? 'ASC');
  }, [pageSize, sortModel]);

  // Debounced auto-search for text fields
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => {
      const next = { ...prev, [field]: value };
      // Trigger search after a short debounce
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setPage(0);
        const sortField = sortModel[0]?.field ?? 'id';
        const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
        const params: Record<string, string> = {};
        Object.entries(next).forEach(([k, v]) => {
          if (v && v.trim() !== '') params[k] = v.trim();
        });
        setIsLoading(true);
        getEuList(pageSize, 0, sortField, sortOrder, params).then((resp) => {
          if (resp?.status === 200) {
            setTableRows(resp.data.rows);
            setTotalTableRows(resp.data.totalRows);
            setIsLoading(false);
          }
        });
      }, 300);
      return next;
    });
  };

  const handleDateSearch = (field: string, value: Date | null) => {
    const dateStr = value && isValid(value) ? format(value, 'yyyy-MM-dd') : '';
    setSearchFilters((prev) => {
      const next = { ...prev, [field]: dateStr };
      // Trigger search immediately on date selection
      setPage(0);
      const sortField = sortModel[0]?.field ?? 'id';
      const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
      const params: Record<string, string> = {};
      Object.entries(next).forEach(([k, v]) => {
        if (v && v.trim() !== '') params[k] = v.trim();
      });
      setIsLoading(true);
      getEuList(pageSize, 0, sortField, sortOrder, params).then((resp) => {
        if (resp?.status === 200) {
          setTableRows(resp.data.rows);
          setTotalTableRows(resp.data.totalRows);
          setIsLoading(false);
        }
      });
      return next;
    });
  };

  const renderSearchHeader = (title: string, field: string, type: 'text' | 'date' = 'text') => {
    if (type === 'date') {
      return (
        <div className="flex flex-col w-full" style={{ minWidth: 0, overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
          <span className="text-xs font-semibold mb-1">{title}</span>
          <DatePicker
            value={searchFilters[field as keyof typeof searchFilters] ? parseISO(searchFilters[field as keyof typeof searchFilters]) : null}
            onChange={(newValue) => handleDateSearch(field, newValue)}
            inputFormat="dd.MM.yyyy."
            OpenPickerButtonProps={{ size: 'small' }}
            components={{
              ActionBar: () => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
                  <Button
                    size="small"
                    onClick={() => handleDateSearch(field, null)}
                    sx={{ textTransform: 'none' }}
                  >
                    Очисти
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleDateSearch(field, new Date())}
                    sx={{ textTransform: 'none' }}
                  >
                    Данас
                  </Button>
                </Box>
              ),
            }}
            renderInput={(params: any) => (
              <TextField
                {...params}
                variant="standard"
                fullWidth
                sx={{
                  width: '100%',
                  '& .MuiInput-root': { width: '100%' },
                  '& .MuiInput-input': { fontSize: '0.75rem', padding: '2px 0' },
                  '& .MuiInput-underline:before': { borderBottomColor: '#81848f3d' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#1D4B8E' },
                  '& .MuiInput-underline:after': { borderBottomColor: '#1D4B8E' },
                  '& .MuiIconButton-root': { padding: '2px' },
                  '& .MuiSvgIcon-root': { fontSize: '0.9rem' },
                }}
              />
            )}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-col w-full" style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
        <span className="text-xs font-semibold mb-1">{title}</span>
        <TextField
          size="small"
          fullWidth
          value={searchFilters[field as keyof typeof searchFilters] ?? ''}
          onChange={(e) => handleSearchChange(field, e.target.value)}
          placeholder="Претрага..."
          variant="standard"
          sx={{
            width: '100%',
            '& .MuiInput-root': { width: '100%' },
            '& .MuiInput-input': { fontSize: '0.75rem', padding: '2px 0' },
            '& .MuiInput-underline:before': { borderBottomColor: '#81848f3d' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#1D4B8E' },
            '& .MuiInput-underline:after': { borderBottomColor: '#1D4B8E' },
          }}
        />
      </div>
    );
  };

  const refreshStatsAndTable = async () => {
    await loadEuStats();
    loadEuList(page * pageSize, pageSize, sortModel[0]?.field ?? 'id', sortModel[0]?.sort?.toUpperCase() ?? 'ASC');
  };

  const startPolling = () => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const resp = await getEuImportStatus();
        if (resp?.status === 200 && resp.data) {
          updateProgress(resp.data);

          if (resp.data.status === 'finished') {
            stopPolling();
            setImportStarted(false);
            notif(
              'Увоз завршен: додато ' + (resp.data.addedRows ?? 0) +
              ', измењено ' + (resp.data.updatedRows ?? 0) + ' редова',
              'success'
            );
            refreshStatsAndTable();
          } else if (resp.data.status === 'failed') {
            stopPolling();
            setImportStarted(false);
            notif('Увоз није успео', 'error');
            refreshStatsAndTable();
          }
        } else {
          stopPolling();
          setImportStarted(false);
        }
      } catch {
        stopPolling();
        setImportStarted(false);
      }
    }, POLL_INTERVAL);
  };

  const handleSubmit = async () => {
    if (selSet) {
      setImportStarted(true);
      setImportStatus(null);
      try {
        await startEuImport(parseInt(selSet));
        startPolling();
      } catch {
        setImportStarted(false);
        notif('Грешка при покретању увоза', 'error');
      }
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, []);

  const loadEuStats = async () => {
    const resp = await getEuStats();

    if (resp.status === 200) {
      setStats(resp?.data);
    }
  };

  const buildSearchParams = (): Record<string, string> => {
    const params: Record<string, string> = {};
    Object.entries(searchFilters).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        params[key] = value.trim();
      }
    });
    return params;
  };

  const loadEuList = async (
    offset: number = 0,
    limit: number = 10,
    sort: string = 'id',
    order: string = 'ASC',
  ) => {
    setIsLoading(true);
    const resp = await getEuList(limit, offset, sort, order, buildSearchParams());
    if (resp?.status === 200) {
      setTableRows(resp.data.rows);
      setTotalTableRows(resp.data.totalRows);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEuStats();
    loadEuList();
  }, []);

  const handleSortModelChange = (newModel: GridSortModel) => {
    // If sort is cleared (neutral state), toggle back to the opposite direction
    if (newModel.length === 0) {
      const prevField = sortModel[0]?.field ?? 'id';
      const prevSort: 'asc' | 'desc' = sortModel[0]?.sort === 'asc' ? 'desc' : 'asc';
      const toggled = [{ field: prevField, sort: prevSort }];
      setSortModel(toggled);
      loadEuList(page * pageSize, pageSize, prevField, prevSort.toUpperCase());
      return;
    }
    setSortModel(newModel);
    const { field, sort } = newModel[0];
    loadEuList(page * pageSize, pageSize, field, sort?.toUpperCase() ?? 'ASC');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    const sortField = sortModel[0]?.field ?? 'id';
    const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
    loadEuList(newPage * pageSize, pageSize, sortField, sortOrder);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
    const sortField = sortModel[0]?.field ?? 'id';
    const sortOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';
    loadEuList(0, newPageSize, sortField, sortOrder);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const columns: GridColumns<any> = [
    {
      field: 'id',
      headerName: '',
      minWidth: 100,
      flex: 0.2,
      sortable: true,
      renderHeader: () => renderSearchHeader('ID', 'id'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'recordId',
      headerName: '',
      minWidth: 120,
      flex: 0.4,
      sortable: true,
      renderHeader: () => renderSearchHeader('Шифра записа', 'recordId'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'celexNo',
      headerName: '',
      minWidth: 60,
      flex: 0.4,
      sortable: true,
      renderHeader: () => renderSearchHeader('CELEX ознака', 'celexNo'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'descriptionEN',
      headerName: '',
      minWidth: 150,
      flex: 1.8,
      sortable: true,
      renderHeader: () => renderSearchHeader('Опис', 'descriptionEN'),
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(params.value == null || String(params.value).trim() === '') ? '/' : params.value}
        </div>
      ),
    },
    {
      field: 'officialJournal',
      headerName: '',
      minWidth: 120,
      flex: 0.8,
      sortable: true,
      renderHeader: () => renderSearchHeader('Службени лист ЕУ', 'officialJournal'),
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(params.value == null || String(params.value).trim() === '') ? '/' : params.value}
        </div>
      ),
    },
    {
      field: 'pages',
      headerName: '',
      minWidth: 80,
      flex: 0.2,
      sortable: true,
      renderHeader: () => renderSearchHeader('Странице', 'pages'),
      renderCell: (params) => (params.value == null || String(params.value).trim() === '') ? '/' : params.value,
    },
    {
      field: 'dateOfPublication',
      headerName: '',
      minWidth: 100,
      flex: 0.5,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум објаве', 'dateOfPublication', 'date'),
      renderCell: (params) => {
        if (!params.value || String(params.value).trim() === '') return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy.') : '/';
      },
    },
    {
      field: 'dateOfImport',
      headerName: '',
      minWidth: 100,
      flex: 0.5,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум увоза', 'dateOfImport', 'date'),
      renderCell: (params) => {
        if (!params.value || String(params.value).trim() === '') return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy.') : '/';
      },
    },
    {
      field: 'dateOfLastUpdate',
      headerName: '',
      minWidth: 100,
      flex: 0.5,
      sortable: true,
      renderHeader: () => renderSearchHeader('Датум измене', 'dateOfLastUpdate', 'date'),
      renderCell: (params) => {
        if (!params.value || String(params.value).trim() === '') return '/';
        const d = parseISO(params.value);
        return isValid(d) ? format(d, 'dd.MM.yyyy.') : '/';
      },
    },

  ];

  function CustomGridFooter(props: any) {
    const totalPages = Math.ceil(props.count / props.pageSize);
    const handlePageSizeChange = (event: any) => {
      props.onPageSizeChange(event.target.value);
    };
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px',
        }}
      >
        <FormControl>
          <InputLabel htmlFor="rows-per-page">Редова по страници</InputLabel>
          <Select
            value={props.pageSize}
            size="small"
            label="Редова по страници"
            sx={{ minWidth: 170 }}
            onChange={handlePageSizeChange}
            inputProps={{
              name: 'rows-per-page',
              id: 'rows-per-page',
            }}
          >
            {props.rowsPerPageOptions.map((option: any) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <p className="text-[#04438b]">Укупно редова: {props.count}</p>
        <Pagination
          shape="rounded"
          count={totalPages}
          page={props.page + 1}
          onChange={(event, newPage) => props.onPageChange(event, newPage - 1)}
          siblingCount={1}
          boundaryCount={1}
        />
      </div>
    );
  }

  const progressPercent =
    importStatus?.totalRows > 0
      ? Math.round((importStatus.processedRows / importStatus.totalRows) * 100)
      : 0;

  return (
    <div className="py-10 px-2 overflow-auto gray-small-scroll h-full">
      <div className="flex gap-6 justify-between pr-10">
        <h1 className="text-[#04438b] text-4xl font-bold">Увоз ЕУ прописа</h1>
        <div
          className="flex items-center justify-between gap-3 rounded-full border border-[#81848f] px-2 text-[#60626a] h-[25px] w-fit  hover:bg-[#303135] hover:text-white cursor-pointer"
          onClick={() => setHelpModal(true)}
        >
          <p className="rounded-full   flex items-center justify-center">
            <FaQuestionCircle />
          </p>
          <p className="">Упутство</p>
        </div>
      </div>
      <div className="flex w-full gap-6">
        <div className="rounded-lg border border-[#81848f3d] w-[500px] p-6 pb-4  mt-8 flex flex-col justify-between">
          <h1 className="text-[#04438bae] text-3xl font-bold mb-6">
            Увоз европских прописа
          </h1>
          <FormControl size="small" sx={{ minWidth: 310 }}>
            <InputLabel id="demo-select-small">Груписано по</InputLabel>
            <Select
              labelId="demo-select-small"
              id="demo-select-small"
              value={selSet ?? ''}
              size="small"
              label="Груписано по"
              onChange={(e) => setSelSet(e.target.value)}
            >
              <MenuItem value="">
                <em>Одабир</em>
              </MenuItem>
              <MenuItem key="2" value="2">
                <strong>2.</strong> Directory
              </MenuItem>
              <MenuItem key="84" value="84">
                <strong>84.</strong> Screening new
              </MenuItem>
            </Select>
          </FormControl>
          <div className="w-full mt-12">
            {importStarted ? (
              <div className="space-y-3">
                {!importStatus?.totalRows ? (
                  <>
                    <LinearProgress />
                    <p className="text-[#04438b] text-sm text-center">
                      Увоз у току... (Овај процес може трајати неколико минута.)
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm text-[#04438b]">
                      <span>
                        Увезено {importStatus.processedRows?.toLocaleString()} / {importStatus.totalRows?.toLocaleString()}
                      </span>
                      <span className="font-bold">{progressPercent}%</span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e8edf5',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: '#1D4B8E',
                        },
                      }}
                    />
                    <div className="flex justify-between text-xs text-[#60626a]">
                      <span>Додато: {importStatus.addedRows ?? 0}</span>
                      <span>Измењено: {importStatus.updatedRows ?? 0}</span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex justify-end">
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#1D4B8E' }}
                  endIcon={<FaUpload />}
                  onClick={handleSubmit}
                  disabled={!selSet}
                >
                  Потврди
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-[#81848f3d] w-[500px] p-6 pb-4 mt-8">
          <h1 className="text-[#04438bae] text-3xl font-bold mb-6">
            Статистика и хронологија увоза
          </h1>

          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Последњи пут увезено:{' '}
            <span className="font-bold">{formatCyDate(stats?.date)}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Груписано по:{' '}
            <span className="font-bold">{stats?.groupSetName}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Увоз извршио корисник:{' '}
            <span className="font-bold">{stats?.name}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Укупнан број редова са EUR-Lex сервера:{' '}
            <span className="font-bold">{stats?.totalRows}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Укупан број додатих редова:{' '}
            <span className="font-bold">{stats?.addedRows}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Укупан број измењених редова:{' '}
            <span className="font-bold">{stats?.updatedRows}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Укупан број редова у табели:{' '}
            <span className="font-bold">{totalTableRows}</span>
          </p>
        </div>
      </div>
      <div ref={tableRef} className="mt-5 h-fit w-full">
        <DataGrid
          rows={tableRows}
          columns={columns}
          loading={isLoading}
          rowCount={totalTableRows}
          pagination
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          page={page}
          pageSize={pageSize}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          style={{ zIndex: 0 }}
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          rowHeight={60}
          autoHeight={true}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              overflow: 'visible',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              overflow: 'visible',
              width: '100%',
            },
            '& .MuiDataGrid-columnHeaderWrapper': {
              overflow: 'visible',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              paddingTop: '8px',
              paddingBottom: '8px',
              minHeight: '60px !important',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
              borderLeft: '3px solid transparent',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(4, 67, 139, 0.18) !important',
              borderLeft: '3px solid #1D4B8E',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'rgba(4, 67, 139, 0.18) !important',
              borderLeft: '3px solid #1D4B8E',
            },
          }}
          getRowId={(row) => row.id}
          onRowClick={(params) => openDetailModal(params.row)}
          components={{
            Row: (props: any) => (
              <Tooltip title="Кликните за детаље" placement="left" arrow followCursor>
                <span style={{ display: 'contents' }}>
                  {/* @ts-ignore */}
                  <DataGridRow {...props} />
                </span>
              </Tooltip>
            ),
            Footer: CustomGridFooter,
            NoRowsOverlay: () => (
              <Stack
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                Не постоје ЕУ прописи
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                Не постоје ЕУ прописи
              </Stack>
            ),
          }}
          componentsProps={{
            footer: {
              count: totalTableRows,
              page: page,
              pageSize: pageSize,
              onPageChange: (event: any, newPage: any) =>
                handlePageChange(newPage),
              onPageSizeChange: (newPageSize: any) =>
                handlePageSizeChange(newPageSize),
              rowsPerPageOptions: [5, 10, 20, 50, 100],
            },
          }}
        />
      </div>
      <Modal
        isOpen={detailModalOpen}
        onRequestClose={closeDetailModal}
        style={{
          overlay: { zIndex: 1500, backgroundColor: 'rgba(0,0,0,0.5)' },
          content: {
            maxWidth: 700,
            width: '90%',
            minHeight: '85vh',
            maxHeight: '90vh',
            margin: 'auto',
            padding: 0,
            border: 'none',
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            overflow: 'auto',
            top: '5%',
          },
        }}
      >
        {selectedRow && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[#04438b] text-2xl font-bold">
                Детаљи ЕУ прописа
              </h2>
              <button
                onClick={closeDetailModal}
                className="text-[#60626a] hover:text-[#303135] text-xl cursor-pointer"
              >
                <FaTimes />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">ID</span>
                <span className="text-[#303135]">{selectedRow.id}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Шифра записа</span>
                <span className="text-[#303135]">{selectedRow.recordId}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">CELEX ознака</span>
                <span className="text-[#303135] font-mono">{selectedRow.celexNo}</span>
              </div>
              <div className="border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium block mb-1">Опис</span>
                <span className="text-[#303135]">{selectedRow.descriptionEN}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Службени лист ЕУ</span>
                <span className="text-[#303135]">{selectedRow.officialJournal}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Странице</span>
                <span className="text-[#303135]">{selectedRow.pages}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Датум објаве</span>
                <span className="text-[#303135]">
                  {selectedRow.dateOfPublication
                    ? (isValid(parseISO(selectedRow.dateOfPublication))
                      ? format(parseISO(selectedRow.dateOfPublication), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Истек ваљаности</span>
                <span className="text-[#303135]">
                  {selectedRow.endOfValidityDate
                    ? (isValid(parseISO(selectedRow.endOfValidityDate))
                      ? format(parseISO(selectedRow.endOfValidityDate), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Статус измене</span>
                <span className="text-[#303135]">{selectedRow.editStat ?? '/'}</span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Датум увоза</span>
                <span className="text-[#303135]">
                  {selectedRow.dateOfImport
                    ? (isValid(parseISO(selectedRow.dateOfImport))
                      ? format(parseISO(selectedRow.dateOfImport), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div className="flex border-b border-[#81848f3d] pb-3">
                <span className="text-[#60626a] font-medium w-[200px] shrink-0">Датум измене</span>
                <span className="text-[#303135]">
                  {selectedRow.dateOfLastUpdate
                    ? (isValid(parseISO(selectedRow.dateOfLastUpdate))
                      ? format(parseISO(selectedRow.dateOfLastUpdate), 'dd.MM.yyyy.')
                      : '/')
                    : '/'}
                </span>
              </div>
              <div>
                <span className="text-[#60626a] font-medium block mb-1">Поглавља</span>
                {selectedRow.chapters && selectedRow.chapters.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedRow.chapters.map((ch: any, idx: number) => (
                      <span
                        key={idx}
                        className="bg-[#e8edf5] text-[#04438b] px-3 py-1 rounded-full text-sm"
                      >
                        {ch.nameEN} ({ch.groupNo})
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-[#303135]">/</span>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
      <HelpPageModal
        modalIsOpen={helpModal}
        closeModal={closeHelpModal}
        name={'insertEU'}
      />
    </div>
  );
}

export default EuInsert;
